<template>
  <div
    class="absolute inset-0 flex flex-col items-center justify-center z-1"
    :class="overlayClasses"
    :style="{ backgroundColor: `rgba(255, 255, 255, ${opacity})` }"
  >
    <NSpin :size="spinSize" :stroke="spinColor" />
    <span v-if="displayMessage" class="font-medium mt-4" :class="textSizeClass" :style="{ color: textColor }">
      {{ displayMessage }}
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { NSpin } from 'naive-ui'
import { useI18n } from 'vue-i18n'
import useThemeVars from '@lib/composables/useThemeVars'

interface Props {
  message?: string
  opacity?: number
  spinSize?: 'small' | 'medium' | 'large'
  spinColor?: string
  textSize?: 'sm' | 'base' | 'lg' | 'xl'
  textColor?: string
  rounded?: boolean
}

const { t } = useI18n()

const props = withDefaults(defineProps<Props>(), {
  message: '',
  opacity: 0.85,
  spinSize: 'large',
  spinColor: '',
  textSize: 'sm',
  textColor: '',
  rounded: true,
})

const themeVars = useThemeVars()

const displayMessage = computed(() => {
  return props.message || t('canvasUI.common.processing')
})

const overlayClasses = computed(() => {
  const classes = []

  if (props.rounded) {
    classes.push('rounded-2')
  }

  return classes.join(' ')
})

const textSizeClass = computed(() => {
  switch (props.textSize) {
    case 'sm':
      return 'text-sm'
    case 'base':
      return 'text-base'
    case 'lg':
      return 'text-lg'
    case 'xl':
      return 'text-xl'
    default:
      return 'text-sm'
  }
})

const spinColor = computed(() => {
  return props.spinColor || themeVars.value.primary3
})

const textColor = computed(() => {
  return props.textColor || themeVars.value.primary3
})
</script>
